import React, { useState, useEffect, useRef } from "react";

const achievements = [
  {
    id: 1,
    spotlight: true,
    title: "International Blockchain Olympiad",
    award: "Silver Medal",
    awardIcon: "🥈",
    medal: "silver",
    event: "IBCOL 2025",
    location: "Hong Kong 🇭🇰",
    date: "January 2025",
    description:
      "Represented Team BIKchain solo on the international stage — selected among Top 10 global finalists and clinched the Silver Medal in Blockchain Category. A journey from Bangladesh to Hong Kong that proved anything is possible.",
    image: `${process.env.PUBLIC_URL}/achievements/HongKong SIlver/sakibahmedshantowithflag.15b49487e034e0e88e1a.png`,
    gradient: "linear-gradient(135deg, #8da9c4, #b8cce0, #d0dce8)",
    glowColor: "rgba(176, 196, 222, 0.5)",
    borderColor: "rgba(192, 192, 192, 0.6)",
    accentColor: "#c0c8d8",
    tag: "INTERNATIONAL",
  },
  {
    id: 2,
    title: "Code Rush 2.0",
    award: "Champion",
    awardIcon: "🏆",
    medal: "gold",
    event: "IUT Flutter Competition",
    location: "IUT, Bangladesh",
    date: "2024",
    description:
      "Battled a brutal 5-hour Flutter bug and emerged victorious. Two-person army — proving perseverance and collaboration win competitions.",
    image: `${process.env.PUBLIC_URL}/achievements/Coderush 2.0/sakibahmedshanto_with_prize_placard.6454b98d4976970252c0.png`,
    gradient: "linear-gradient(135deg, #f7971e, #ffd200, #f7971e)",
    glowColor: "rgba(255, 215, 0, 0.45)",
    borderColor: "rgba(255, 200, 0, 0.6)",
    accentColor: "#ffd200",
    tag: "1ST PLACE",
  },
  {
    id: 3,
    title: "Soccer Bot Competition",
    award: "1st Position",
    awardIcon: "🥇",
    medal: "gold",
    event: "IEEERAS IUT — Program Accelerator 2.0",
    location: "IUT, Bangladesh",
    date: "2023",
    description:
      "Built an autonomous soccer-playing robot from scratch. Champions through innovative design, mechanical expertise, circuit implementation, and flawless execution.",
    image: `${process.env.PUBLIC_URL}/achievements/soccerbot IEEERAS/sakibahmedshanto_prizetaking_soccerbot.acd84bcc417291fa8800.png`,
    gradient: "linear-gradient(135deg, #f7971e, #ffd200, #f7971e)",
    glowColor: "rgba(255, 200, 0, 0.4)",
    borderColor: "rgba(255, 200, 0, 0.5)",
    accentColor: "#ffd200",
    tag: "1ST PLACE",
  },
  {
    id: 4,
    title: "Code Rush 1.0",
    award: "1st Runner-Up",
    awardIcon: "🥈",
    medal: "silver",
    event: "IUT Project Showcase",
    location: "IUT, Bangladesh",
    date: "2023",
    description:
      "Presented an innovative project that impressed judges with technical implementation, creativity, and real-world applicability.",
    image: `${process.env.PUBLIC_URL}/achievements/coderush 1.0/projectshowcasing.7e722142a1fbc772208e.png`,
    gradient: "linear-gradient(135deg, #8da9c4, #b8cce0, #c8d8e8)",
    glowColor: "rgba(176, 196, 222, 0.35)",
    borderColor: "rgba(192, 192, 192, 0.5)",
    accentColor: "#c0c8d8",
    tag: "2ND PLACE",
  },
  {
    id: 5,
    title: "Hackathon Automech",
    award: "2nd Runner-Up",
    awardIcon: "🥉",
    medal: "bronze",
    event: "IUT Hackathon",
    location: "IUT, Bangladesh",
    date: "2024",
    description:
      "Team Robolink secured 2nd Runner-Up among 25 participants (only 8 made finals). Built a revolutionary mobile app transforming robotics development.",
    image: `${process.env.PUBLIC_URL}/achievements/Hackhathon Automech/sakibahmedshanto_standing_with_prize_placard.ed3b75cdf57ab99bba40.png`,
    gradient: "linear-gradient(135deg, #cd7f32, #e8a957, #cd7f32)",
    glowColor: "rgba(205, 127, 50, 0.35)",
    borderColor: "rgba(205, 127, 50, 0.5)",
    accentColor: "#e8a957",
    tag: "3RD PLACE",
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function SpotlightCard({ achievement }) {
  const [ref, inView] = useInView(0.1);
  return (
    <div
      ref={ref}
      className={`ach-spotlight-card${inView ? " ach-animate-in" : ""}`}
      style={{ "--glow": achievement.glowColor, "--border": achievement.borderColor, "--accent": achievement.accentColor }}
    >
      {/* Shimmer border */}
      <div className="ach-shimmer-border" />

      {/* International tag */}
      <div className="ach-spotlight-tag">{achievement.tag}</div>

      <div className="ach-spotlight-inner">
        {/* Image side */}
        <div className="ach-spotlight-img-wrap">
          <div className="ach-spotlight-img-glow" style={{ background: `radial-gradient(circle, ${achievement.glowColor} 0%, transparent 70%)` }} />
          <img
            src={achievement.image}
            alt={achievement.title}
            className="ach-spotlight-img"
            onError={(e) => { e.target.style.opacity = "0"; }}
          />
          <div className="ach-spotlight-medal-badge">
            <span className="ach-medal-emoji">{achievement.awardIcon}</span>
            <span className="ach-medal-text">{achievement.award}</span>
          </div>
        </div>

        {/* Content side */}
        <div className="ach-spotlight-content">
          <div className="ach-spotlight-meta">
            <span className="ach-spotlight-event">{achievement.event}</span>
            <span className="ach-spotlight-location">{achievement.location}</span>
          </div>

          <h3 className="ach-spotlight-title">{achievement.title}</h3>

          <p className="ach-spotlight-desc">{achievement.description}</p>

          <div className="ach-spotlight-footer">
            <span className="ach-spotlight-date">{achievement.date}</span>
            <div className="ach-award-pill" style={{ background: `linear-gradient(135deg, ${achievement.accentColor}22, ${achievement.accentColor}11)`, borderColor: achievement.borderColor }}>
              <span>{achievement.awardIcon}</span>
              <span style={{ color: achievement.accentColor }}>{achievement.award}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AchCard({ achievement, delay }) {
  const [ref, inView] = useInView(0.1);
  const [hovered, setHovered] = useState(false);
  return (
    <div
      ref={ref}
      className={`ach-card-v2${inView ? " ach-animate-in" : ""}${hovered ? " ach-card-hovered" : ""}`}
      style={{ "--glow": achievement.glowColor, "--border": achievement.borderColor, "--accent": achievement.accentColor, animationDelay: `${delay}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="ach-card-shimmer" />

      {/* Top image strip */}
      <div className="ach-card-img-strip">
        <img
          src={achievement.image}
          alt={achievement.title}
          className="ach-card-img"
          onError={(e) => { e.target.style.opacity = "0"; }}
        />
        <div className="ach-card-img-overlay" style={{ background: `linear-gradient(to top, #060a18 0%, transparent 60%)` }} />
        <div className="ach-card-tag">{achievement.tag}</div>
      </div>

      {/* Content */}
      <div className="ach-card-body">
        <div className="ach-card-award-row">
          <span className="ach-card-emoji">{achievement.awardIcon}</span>
          <span className="ach-card-award-label" style={{ color: achievement.accentColor }}>{achievement.award}</span>
        </div>

        <h4 className="ach-card-title">{achievement.title}</h4>
        <span className="ach-card-event">{achievement.event}</span>
        <p className="ach-card-desc">{achievement.description}</p>

        <div className="ach-card-footer">
          <span className="ach-card-date">{achievement.date}</span>
          <span className="ach-card-location">{achievement.location}</span>
        </div>
      </div>

      {/* Glow bar at bottom */}
      <div className="ach-card-glow-bar" style={{ background: `linear-gradient(90deg, transparent, ${achievement.accentColor}, transparent)` }} />
    </div>
  );
}

function StatCounter({ value, label, icon, color }) {
  const [ref, inView] = useInView(0.2);
  return (
    <div ref={ref} className={`ach-stat${inView ? " ach-animate-in" : ""}`}>
      <div className="ach-stat-icon">{icon}</div>
      <div className="ach-stat-value" style={{ color }}>{value}</div>
      <div className="ach-stat-label">{label}</div>
    </div>
  );
}

function AchievementsSection() {
  const spotlight = achievements.find((a) => a.spotlight);
  const rest = achievements.filter((a) => !a.spotlight);

  return (
    <section id="achievements" className="ach-section">
      {/* Dramatic background orbs */}
      <div className="ach-bg-orb ach-bg-orb-gold" />
      <div className="ach-bg-orb ach-bg-orb-silver" />
      <div className="ach-bg-orb ach-bg-orb-purple" />

      <div className="sp-container">
        {/* Section header */}
        <div className="sp-section-header">
          <span className="ach-section-label">HALL OF FAME</span>
          <h2 className="section-title" style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)" }}>
            Awards &amp; <span className="ach-gradient-gold">Recognition</span>
          </h2>
          <p className="section-subtitle">
            International medals, national championships, and competition victories — a track record of excellence.
          </p>
        </div>

        {/* Stats row */}
        <div className="ach-stats-row">
          <StatCounter value="1" label="International Medal" icon="🌍" color="#c0c8d8" />
          <div className="ach-stats-divider" />
          <StatCounter value="2" label="1st Place Wins" icon="🏆" color="#ffd200" />
          <div className="ach-stats-divider" />
          <StatCounter value="5" label="Total Awards" icon="🎖️" color="#a78bfa" />
          <div className="ach-stats-divider" />
          <StatCounter value="3+" label="Countries Competed" icon="🌐" color="#06b6d4" />
        </div>

        {/* Spotlight: Hong Kong Silver */}
        <SpotlightCard achievement={spotlight} />

        {/* Sub-heading for rest */}
        <div className="ach-more-header">
          <div className="ach-more-line" />
          <span className="ach-more-label">More Victories</span>
          <div className="ach-more-line" />
        </div>

        {/* Other achievement cards */}
        <div className="ach-cards-grid">
          {rest.map((a, i) => (
            <AchCard key={a.id} achievement={a} delay={i * 100} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default AchievementsSection;
